import { useState } from 'react';

import { ImagePickerModal } from './ImagePickerModal';
import { isOwnStorageUrl } from './storageUrl';

export interface ImageUrlFieldProps {
  id?: string;
  label: React.ReactNode;
  value: string;
  onChange: (url: string) => void;
  /** Alt text the picker suggests (from the file name or the curator), when the caller has an alt field. */
  onAltSuggested?: (alt: string) => void;
  placeholder?: string;
  /** Classes for the URL input, so the host form keeps its look. */
  inputClassName?: string;
  labelClassName?: string;
}

/**
 * An image field that takes a URL OR an upload to our own Storage.
 *
 * The URL input stays — pasting an existing public URL is still a real path —
 * and "Upload image…" opens the same ImagePickerModal the rich-text editor
 * uses (upload → `admin/richTextImages/`, resized, public-read; or a URL). The
 * field's value is always a plain URL, so whatever document it belongs to
 * keeps its existing shape. A badge says whether the current image lives in
 * our Storage or on someone else's site.
 */
export function ImageUrlField({
  id,
  label,
  value,
  onChange,
  onAltSuggested,
  placeholder = 'https://...',
  inputClassName = 'mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500',
  labelClassName = 'block text-sm font-medium text-gray-900',
}: ImageUrlFieldProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);
  const url = value.trim();
  const own = url
    ? isOwnStorageUrl(url, import.meta.env.VITE_FIREBASE_STORAGE_BUCKET as string | undefined)
    : false;

  return (
    <div>
      <label htmlFor={id} className={labelClassName}>
        {label}
      </label>
      <div className="flex items-center gap-2">
        <input
          id={id}
          type="url"
          value={value}
          onChange={(e) => {
            setPreviewFailed(false);
            onChange(e.target.value);
          }}
          placeholder={placeholder}
          className={inputClassName}
        />
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          className="mt-1 shrink-0 rounded-lg bg-blue-50 px-3 py-2 text-xs font-medium text-blue-700 hover:bg-blue-100"
        >
          {url ? 'Replace…' : 'Upload image…'}
        </button>
      </div>
      {url && (
        <div className="mt-2 flex items-center gap-3">
          {!previewFailed ? (
            <img
              src={url}
              alt=""
              className="h-16 w-16 rounded border border-gray-200 object-cover"
              onError={() => setPreviewFailed(true)}
            />
          ) : (
            <span className="text-xs text-red-600">Image did not load</span>
          )}
          {own ? (
            <span className="rounded bg-green-50 px-1.5 py-0.5 text-[11px] font-medium text-green-700">
              Stored in Watch Keeper Storage
            </span>
          ) : (
            <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-800">
              External URL — upload a copy so it can&rsquo;t disappear
            </span>
          )}
        </div>
      )}
      <ImagePickerModal
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        initial={url ? { src: url, alt: '' } : undefined}
        initialTab="upload"
        onSelect={({ src, alt }) => {
          setPickerOpen(false);
          setPreviewFailed(false);
          onChange(src);
          if (alt.trim()) onAltSuggested?.(alt.trim());
        }}
      />
    </div>
  );
}
